import React, { useRef, useEffect, useState } from "react";
import { Button, ButtonProps } from "@/components/ui/button";
import { Heart, Loader2 } from "lucide-react";
import { useLikeProfile } from "@/hooks/profile/useLikeProfile";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface LikeButtonProps extends Omit<ButtonProps, "onClick"> {
  profileId: string;
  showCount?: boolean;
  iconOnly?: boolean;
  size?: "sm" | "default" | "lg" | "icon";
  variant?: "default" | "secondary" | "ghost" | "outline";
  className?: string;
  onLikeChange?: (liked: boolean, count: number) => void;
}

export const LikeButton: React.FC<LikeButtonProps> = ({
  profileId,
  showCount = false,
  iconOnly = false,
  size = "default",
  variant = "default",
  className,
  onLikeChange,
  ...buttonProps
}) => {
  const { isLiked, likeCount, toggleLike, isLoading, error } = useLikeProfile({
    profileId,
  });
  const prevIsLiked = useRef(isLiked);
  const prevLikeCount = useRef(likeCount);

  // Local state to provide immediate visual feedback
  const [isAnimating, setIsAnimating] = useState(false);
  const [displayCount, setDisplayCount] = useState(likeCount);

  // Update display count when likeCount changes
  useEffect(() => {
    setDisplayCount(likeCount);
  }, [likeCount]);

  // Call the onLikeChange callback only when values actually change
  useEffect(() => {
    // Only trigger callback if values have actually changed and callback exists
    if (
      onLikeChange &&
      (prevIsLiked.current !== isLiked || prevLikeCount.current !== likeCount)
    ) {
      onLikeChange(isLiked, likeCount);

      // Update refs to current values
      prevIsLiked.current = isLiked;
      prevLikeCount.current = likeCount;
    }
  }, [isLiked, likeCount, onLikeChange]);

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (isLoading) return;

    // Start animation
    setIsAnimating(true);

    try {
      await toggleLike(e);
    } catch (error) {
      console.error("[LikeButton] Error during like toggle:", error);
    } finally {
      // Stop animation after a short delay to allow visual feedback
      setTimeout(() => {
        setIsAnimating(false);
      }, 300);
    }
  };

  if (!profileId) {
    return <Skeleton className="h-10 w-24" />;
  }

  const isButtonLoading = isLoading || isAnimating;
  const buttonVariant = isLiked ? "secondary" : variant;

  return (
    <Button
      variant={buttonVariant}
      size={size}
      onClick={handleClick}
      disabled={isButtonLoading}
      className={cn(
        "relative transition-all duration-200",
        isLiked && "bg-red-50 border-red-200 text-red-600 hover:bg-red-100",
        isAnimating && "scale-95",
        className
      )}
      {...buttonProps}
    >
      {isButtonLoading ? (
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
      ) : (
        <Heart
          className={cn(
            "h-4 w-4 transition-all duration-200",
            isLiked && "fill-current text-red-500",
            showCount && !iconOnly && "mr-2",
            isAnimating && "scale-110"
          )}
        />
      )}
      {!iconOnly && (
        <>
          {showCount ? (
            <span className="font-medium">
              {displayCount} {displayCount === 1 ? "Like" : "Likes"}
            </span>
          ) : (
            <span className="font-medium">{isLiked ? "Liked" : "Like"}</span>
          )}
        </>
      )}

      {/* Error indicator */}
      {error && (
        <span className="absolute -top-1 -right-1 h-2 w-2 bg-red-500 rounded-full" />
      )}
    </Button>
  );
};
